/**
 * Cliente HTTP del feature "Escuchar & Hablar" (`/api/listen/*`).
 *
 * Mismo patrón que `lib/englishApi.ts`: `fetch` con `credentials:'include'`
 * (cookie httoOnly same-origin vía proxy de Vite), envelope `{ success, data }`.
 * Endpoints (backend `apps/backend/src/routes/listen.ts`):
 *
 *   GET   /api/listen/catalog
 *   GET   /api/listen/chapter/:book/:edition/:keypoint?cefr=&lang=&voice=
 *   GET   /api/listen/coverage/:book/:edition?cefr=            (ficha: N capítulos, sólo números)
 *   GET   /api/listen/coverage/:book/:edition/:keypoint?cefr=  (player: 1 capítulo + palabras)
 *   GET   /api/listen/progress[?book=]
 *   PATCH /api/listen/progress
 *
 * El `/chapter` devuelve 404 `rendition_not_available` + `available[]` cuando no
 * hay rendition para la combinación pedida (NO cae a un default silencioso —
 * regla fail-fast). Lo modelamos como `ListenApiError` con `available` adjunto
 * para que el caller (store) reaccione (selector "is-locked").
 */

import { createTypedApiClient, TypedApiError } from '@orborix/auth-client';

import type { AlignmentJson } from './alignment.js';
import type {
  Availability,
  CatalogBook,
  ChapterResponse,
  Cefr,
  Edition,
  ProgressEntry,
  Rendition,
} from './types.js';

const BASE = '/api/listen';

/** Error con código crudo del backend + `available` opcional (404 rendition).
 *  El 402 de `/unlock` adjunta `balanceNano`/`priceNano` (prompt de saldo, D3).
 *  Los tres se leen del body crudo del error, que la base guarda en `details`. */
export class ListenApiError extends TypedApiError {
  readonly available?: Availability[];
  readonly balanceNano?: string;
  readonly priceNano?: string;
  constructor(
    message: string,
    status: number,
    code: string,
    details: Record<string, unknown> = {},
  ) {
    super(message, status, code, details);
    this.name = 'ListenApiError';
    if (details.available) this.available = details.available as Availability[];
    if (details.balanceNano) this.balanceNano = details.balanceNano as string;
    if (details.priceNano) this.priceNano = details.priceNano as string;
  }
}

const client = createTypedApiClient({
  basePath: BASE,
  errorName: 'ListenApiError',
  abortError: 'wrap',
  errorClass: ListenApiError,
});

interface Envelope<T> {
  success?: boolean;
  data?: T;
  error?: string;
  available?: Availability[];
  balanceNano?: string;
  priceNano?: string;
}

/**
 * Desenvuelto **estricto** (`body.data`), con el body parseado UNA vez: el
 * mismo objeto alimenta los campos extra del error (`available`, `balanceNano`,
 * `priceNano`) y los datos. Por eso no usa el `get` del cliente compartido.
 */
async function jsonFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await client.request(path, init);
  const body = (await res.json().catch(() => ({}))) as Envelope<T>;
  if (!res.ok) {
    throw new ListenApiError(
      body.error ?? `HTTP ${res.status}`,
      res.status,
      body.error ?? 'unknown',
      body as Record<string, unknown>,
    );
  }
  return body.data as T;
}

// ─── Catálogo ───────────────────────────────────────────────────────────────

export function getCatalog(): Promise<{ books: CatalogBook[] }> {
  return jsonFetch<{ books: CatalogBook[] }>('/catalog');
}

// ─── Capítulo (resuelve 1 rendition) ────────────────────────────────────────

export interface ChapterQuery {
  cefr?: Cefr;
  lang?: string;
  voice?: string;
}

export function getChapter(
  ref: { book: string; edition: Edition; keypoint: string },
  query: ChapterQuery = {},
): Promise<ChapterResponse> {
  const qs = new URLSearchParams();
  if (query.cefr) qs.set('cefr', query.cefr);
  if (query.lang) qs.set('lang', query.lang);
  if (query.voice) qs.set('voice', query.voice);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return jsonFetch<ChapterResponse>(
    `/chapter/${encodeURIComponent(ref.book)}/${ref.edition}/${encodeURIComponent(ref.keypoint)}${suffix}`,
  );
}

// ─── Unlock de capítulo (`POST /api/listen/unlock`, Fase 22) ─────────────────

export interface UnlockResponse {
  unlocked: true;
  /** true = no se cobró (ya estaba desbloqueado o es el sample gratis). */
  alreadyUnlocked: boolean;
  /** Nano-USD cobrados en ESTE unlock ("0" si already). */
  charged: string;
  /** Saldo post-cobro, o null si no hubo débito. */
  newBalanceNano: string | null;
  /** Rendition pedida ya firmada (evita un segundo round-trip), o null si la
   *  combinación cefr/lang/voice no existe — el caller cae a `getChapter`. */
  rendition: Rendition | null;
}

/**
 * Cobra (idempotente) y desbloquea un capítulo. 402 `insufficient_credits`
 * llega como `ListenApiError` con `balanceNano`/`priceNano` para el prompt
 * de carga de saldo (D3).
 */
export function postUnlock(
  ref: { book: string; edition: Edition; keypoint: string },
  query: ChapterQuery = {},
): Promise<UnlockResponse> {
  return jsonFetch<UnlockResponse>('/unlock', {
    method: 'POST',
    body: JSON.stringify({ ...ref, ...query }),
  });
}

// ─── Cobertura del capítulo (`GET /api/listen/coverage/...`) ────────────────

/** Banda de presentación resuelta por el backend (la tabla visual vive en el front). */
export type CoverageBandId = 'alto' | 'medio' | 'bajo';

/** Una palabra de contenido que el user todavía NO maneja (panel de explorar). */
export interface CoverageUnknown {
  /** Forma a mostrar (tal como aparece en el transcript). */
  word: string;
  lemma: string;
  /** Oración de contexto (para el panel y el lookup). */
  sentence: string;
}

/**
 * Los NÚMEROS de la cobertura, sin la lista de palabras. Es todo lo que
 * necesita la ficha del libro (anillo + `%`), y lo único que viaja en la
 * respuesta por libro.
 */
export interface CoverageStats {
  pct: number;
  band: CoverageBandId;
  contentCount: number;
  knownCount: number;
  unknownCount: number;
}

/** Cobertura COMPLETA de un capítulo: los números + las palabras a explorar. */
export interface CoverageData extends CoverageStats {
  unknown: CoverageUnknown[];
}

/** Envelope `data` crudo: `available:false` cuando no hay transcript en inglés. */
type CoveragePayload = { available: false } | ({ available: true } & CoverageData);

/**
 * Cobertura del capítulo al nivel `cefr`. Devuelve `null` cuando el backend
 * responde `{ available:false }` (sin transcript en inglés) — el caller omite
 * toda la UI de cobertura en ese caso (no inventa nada).
 */
export async function getChapterCoverage(
  ref: { book: string; edition: Edition; keypoint: string },
  cefr: Cefr,
): Promise<CoverageData | null> {
  const data = await jsonFetch<CoveragePayload>(
    `/coverage/${encodeURIComponent(ref.book)}/${ref.edition}/${encodeURIComponent(ref.keypoint)}?cefr=${cefr}`,
  );
  if (!data.available) return null;
  const { available: _ignored, ...coverage } = data;
  return coverage;
}

/** Una fila de `GET /coverage/:book/:edition`: el capítulo y, si mide, sus números. */
type BookCoverageChapter =
  | { keypoint: string; available: false }
  | ({ keypoint: string; available: true } & CoverageStats);

/**
 * Cobertura de TODOS los capítulos del libro en UN pedido, sin la lista de
 * palabras a explorar — la ficha sólo pinta `pct`/`band` por fila, y esa lista
 * (una por capítulo, con la oración de contexto de cada palabra) pesaba dos
 * órdenes de magnitud más que los números que se ven.
 *
 * Devuelve un mapa slug→stats con **una entrada por capítulo del libro**:
 * `null` = el backend dijo que ese capítulo no mide cobertura (sin transcript
 * en inglés a ese nivel). Un slug AUSENTE del mapa no es lo mismo: significa
 * que el backend no lo mencionó, y el caller tiene que notarlo en vez de
 * confundirlo con "no mide".
 */
export async function getBookCoverage(
  ref: { book: string; edition: Edition },
  cefr: Cefr,
): Promise<Record<string, CoverageStats | null>> {
  const data = await jsonFetch<{ chapters: BookCoverageChapter[] }>(
    `/coverage/${encodeURIComponent(ref.book)}/${ref.edition}?cefr=${cefr}`,
  );
  const byKp: Record<string, CoverageStats | null> = {};
  for (const ch of data.chapters) {
    if (!ch.available) {
      byKp[ch.keypoint] = null;
      continue;
    }
    const { keypoint: _kp, available: _av, ...stats } = ch;
    byKp[ch.keypoint] = stats;
  }
  return byKp;
}

// ─── Progreso ─────────────────────────────────────────────────────────────

export function getProgress(book?: string): Promise<{ progress: ProgressEntry[] }> {
  const suffix = book ? `?book=${encodeURIComponent(book)}` : '';
  return jsonFetch<{ progress: ProgressEntry[] }>(`/progress${suffix}`);
}

export interface ProgressPatch {
  book: string;
  edition: Edition;
  keypoint: string;
  lastPositionSec?: number;
  listened?: boolean;
  reviewed?: boolean;
  conversed?: boolean;
  cefr?: Cefr;
}

/**
 * `keepalive` es para el último PATCH antes de cerrar/ocultar la pestaña: sin
 * él el browser cancela el request en el unload y se pierden los últimos
 * segundos escuchados (el PATCH normal va throttled a 5s).
 */
export function patchProgress(
  patch: ProgressPatch,
  opts: { keepalive?: boolean } = {},
): Promise<void> {
  return jsonFetch<unknown>('/progress', {
    method: 'PATCH',
    body: JSON.stringify(patch),
    ...(opts.keepalive ? { keepalive: true } : {}),
  }).then(() => undefined);
}

/** Carga + parsea el `alignment.json` de una rendition (URL relativa o absoluta).
 *
 * Asset PÚBLICO: NO mandamos `credentials` a propósito. En staging/prod la URL
 * es cross-origin (bucket Tigris con CORS `Access-Control-Allow-Origin: *`); un
 * fetch credentialed contra un CORS wildcard lo bloquea el browser (exige origin
 * explícito + `Allow-Credentials: true`), tirando el karaoke a "transcripción no
 * disponible". El default `same-origin` manda la cookie sólo en dev (same-origin)
 * y la omite contra Tigris. */
export async function fetchAlignment(url: string): Promise<AlignmentJson> {
  const res = await fetch(url);
  if (!res.ok) throw new ListenApiError(`alignment HTTP ${res.status}`, res.status, 'alignment_fetch');
  return (await res.json()) as AlignmentJson;
}
